import { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { MetricType } from 'components/common/types/DrilldownReports.types';
import { download } from 'utils/download';
import { cancelFunctions } from 'utils/cancelFunctions';
import { exportDirectSalesAvailabilityReport } from 'modules/api/availabilityReport';
import { addToastNotification } from 'store/notification/reducer';
import { getMarketId } from 'store/publisher/selectors';
import { AvailabilityReportFilter, AvailabilityGranularity } from './AvailabilityReport.types';
import { transformAvailabilityRequestPayload } from './transformAvailabilityRequestPayload';
import { getAvailabilityExportFileName } from './utils';

export const useExportAvailabilityReport = (): {
  exportAvailabilityReport: (
    selectedFilters: AvailabilityReportFilter,
    selectedMetricType: MetricType,
    granularity: AvailabilityGranularity,
  ) => Promise<void>;
  isExporting: boolean;
} => {
  const dispatch = useDispatch();
  const marketId = useSelector(getMarketId);
  const [isExporting, setIsExporting] = useState(false);

  const exportAvailabilityReport = async (
    selectedFilters: AvailabilityReportFilter,
    selectedMetricType: MetricType,
    granularity: AvailabilityGranularity,
  ): Promise<void> => {
    const transformedData = transformAvailabilityRequestPayload(selectedFilters, marketId);

    if (!transformedData) {
      return;
    }

    const payload = {
      ...transformedData,
      granularity,
      metric: selectedMetricType,
    };

    setIsExporting(true);

    try {
      const result = await exportDirectSalesAvailabilityReport(marketId, payload, cancelFunctions);

      const csvBlob = new Blob([result.data], { type: 'text/csv;charset=utf-8;' });

      download(csvBlob, getAvailabilityExportFileName('full-availability-report', 'csv'));
    } catch (error) {
      dispatch(
        addToastNotification({
          header: 'Export failed',
          description: 'Failed to export availability report. Please try again.',
          type: 'Failure',
          timeout: 10000,
        }),
      );
    } finally {
      setIsExporting(false);
    }
  };

  return {
    exportAvailabilityReport,
    isExporting,
  };
};
